import { Box, ButtonIcon, ButtonLink, Flex, Text } from '@backstage/ui';
import { RiArrowRightLine, RiCloseLine, RiCpuLine } from '@remixicon/react';
import {
  useEffect,
  useMemo,
  useState,
  useRef,
  type CSSProperties,
} from 'react';
import {
  configApiRef,
  storageApiRef,
  useAnalytics,
  useApi,
} from '@backstage/core-plugin-api';

type MessageOfTheDayCategory = 'General' | 'Testing' | 'Product Release';

type MessageOfTheDayConfig = {
  id: string;
  category?: MessageOfTheDayCategory;
  title: string;
  message: string;
  linkText: string;
  link: string;
  variant?: 'info' | 'warning' | 'error' | 'success';
  enabled?: boolean;
};

const STORAGE_BUCKET = 'message-of-the-day';
const DISMISSED_MESSAGE_ID_KEY = 'dismissed-message-id';

function useMessageOfTheDayConfig(): MessageOfTheDayConfig | undefined {
  const config = useApi(configApiRef);
  const messageConfig = config.getOptionalConfig('app.messageOfTheDay');

  if (!messageConfig) {
    return undefined;
  }

  if (messageConfig.getOptionalBoolean('enabled') === false) {
    return undefined;
  }

  return {
    id: messageConfig.getString('id'),
    category: messageConfig.getOptionalString('category') as
      | MessageOfTheDayCategory
      | undefined,
    title: messageConfig.getString('title'),
    message: messageConfig.getString('message'),
    linkText: messageConfig.getString('linkText'),
    link: messageConfig.getString('link'),
    variant: messageConfig.getOptionalString('variant') as
      | MessageOfTheDayConfig['variant']
      | undefined,
  };
}

// The banner keeps its brand-tinted surface, with the icon tile and category
// outlined in the brand tint; on the shared Make brand tokens so it follows
// light and dark mode.
const ROOT: CSSProperties = {
  position: 'relative',
  width: '100%',
  padding:
    'var(--bui-space-4) var(--bui-space-10) var(--bui-space-4) var(--bui-space-4)',
  borderRadius: 'var(--bui-radius-2)',
  border: '1px solid var(--bui-border-1)',
  backgroundColor: 'var(--make-bg-brand-subtle)',
};

const ICON_TILE: CSSProperties = {
  width: 46,
  height: 46,
  borderRadius: 'var(--bui-radius-2)',
  border: '1px solid var(--make-border-brand-subtle)',
  color: 'var(--make-fg-brand)',
  flexShrink: 0,
};

const CATEGORY: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 24,
  padding: '0 var(--bui-space-2)',
  borderRadius: 'var(--bui-radius-full)',
  border: '1px solid var(--make-border-brand-subtle)',
  color: 'var(--make-fg-brand)',
  fontSize: 'var(--bui-font-size-2)',
  fontWeight: 700,
};

// BUI 0.18's Flex has no wrap prop; the title row and the message wrap.
const WRAP: CSSProperties = { flexWrap: 'wrap' };

function getAnalyticsAttributes(message: MessageOfTheDayConfig) {
  return {
    category: message.category ?? 'General',
    link: message.link,
    linkText: message.linkText,
    variant: message.variant ?? 'info',
  };
}

export function MessageOfTheDay() {
  const message = useMessageOfTheDayConfig();

  const storageApi = useApi(storageApiRef);
  const storage = useMemo(
    () => storageApi.forBucket(STORAGE_BUCKET),
    [storageApi],
  );

  const [dismissalState, setDismissalState] = useState(() => {
    const snapshot = storage.snapshot<string>(DISMISSED_MESSAGE_ID_KEY);

    return {
      dismissedMessageId:
        snapshot.presence === 'present' ? snapshot.value : undefined,
      hydrated: snapshot.presence !== 'unknown',
    };
  });

  const { dismissedMessageId, hydrated } = dismissalState;

  useEffect(() => {
    const subscription = storage
      .observe$<string>(DISMISSED_MESSAGE_ID_KEY)
      .subscribe({
        next: snapshot => {
          setDismissalState({
            dismissedMessageId:
              snapshot.presence === 'present' ? snapshot.value : undefined,
            hydrated: true,
          });
        },
      });

    return () => subscription.unsubscribe();
  }, [storage]);

  const isDismissed = Boolean(message?.id && dismissedMessageId === message.id);

  const analytics = useAnalytics();
  const trackedViewMessageIds = useRef(new Set<string>());

  useEffect(() => {
    if (
      !message ||
      !hydrated ||
      isDismissed ||
      trackedViewMessageIds.current.has(message.id)
    ) {
      return;
    }

    trackedViewMessageIds.current.add(message.id);
    analytics.captureEvent('motd_view', message.id, {
      attributes: getAnalyticsAttributes(message),
    });
  }, [analytics, hydrated, isDismissed, message]);

  if (!message || !hydrated || isDismissed) {
    return null;
  }

  const handleClick = () => {
    analytics.captureEvent('motd_click', message.id, {
      attributes: getAnalyticsAttributes(message),
    });
  };

  const handleDismiss = () => {
    analytics.captureEvent('motd_dismiss', message.id, {
      attributes: getAnalyticsAttributes(message),
    });

    setDismissalState({
      dismissedMessageId: message.id,
      hydrated: true,
    });
    void storage.set(DISMISSED_MESSAGE_ID_KEY, message.id);
  };

  return (
    <Flex gap="3" align="start" style={ROOT}>
      <Flex align="center" justify="center" style={ICON_TILE}>
        <RiCpuLine size={26} aria-hidden />
      </Flex>

      <Box style={{ minWidth: 0, flex: 1 }}>
        <Flex align="center" gap="2" mb="1" style={WRAP}>
          <span style={CATEGORY}>{message.category ?? 'General'}</span>
          <Text as="h2" variant="body-large" weight="bold">
            {message.title}
          </Text>
        </Flex>

        <Flex align="center" gap="2" style={WRAP}>
          <Text as="span" variant="body-medium" color="secondary">
            {message.message}
          </Text>

          <ButtonLink
            href={message.link}
            size="small"
            variant="primary"
            iconEnd={<RiArrowRightLine />}
            noTrack
            onPress={handleClick}
          >
            {message.linkText}
          </ButtonLink>
        </Flex>
      </Box>

      <ButtonIcon
        size="small"
        variant="tertiary"
        aria-label="Dismiss message"
        icon={<RiCloseLine />}
        onPress={handleDismiss}
        style={{
          position: 'absolute',
          top: 'var(--bui-space-2)',
          right: 'var(--bui-space-2)',
          color: 'var(--bui-fg-disabled)',
        }}
      />
    </Flex>
  );
}
